import React, { useEffect, useRef } from 'react';
import { Range, getTrackBackground } from 'react-range';
import { Button, Drawer } from '../../../../../ui';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import { StatusToggle } from '../../Interviews/Containers/Candidates/Components';
import ColorRangeSelector, { ColorRangeSelectorRef } from '../UI/ColorRangeSelector';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

type Analytic3DProps = {
    isOpen: boolean,
    onCloseDrawer: () => void,
    scatter: boolean,
    setScatter: (value: boolean) => void,
    view3D: boolean,
    setView3D: (value: boolean) => void,
    markLines: boolean,
    setMarkLines: (value: boolean) => void,
    nineBox: boolean,
    setNineBox: (value: boolean) => void,
    setSimilarKeyword: React.Dispatch<React.SetStateAction<string>>,
    similarKeyword: string,
    setExactKeyword: React.Dispatch<React.SetStateAction<string>>,
    exactKeyword: string,
    setKeywordList: (
        value: ((prevState: string[]) => string[]) | string[]
    ) => void,
    colorList: string[],
    setColorList: (value: (((prevState: string[]) => string[]) | string[])) => void,
    useSkillPercentage: boolean,
    setUseSkillPercentage: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    setExperienceValue: (value: number[]) => void,
    skillValue: number[],
    setSkillValue: (value: number[]) => void,
    experienceValue: number[],
    expTitle: string,
    skillTitle: string,
    setSvgActiveTab: (value: (((prevState: number) => number) | number)) => void,
    projection: string,
    setProjection: (value: (((prevState: string) => string) | string)) => void,
    boxWidthDepth: number[],
    setBoxWidthDepth: (value: (((prevState: number[]) => number[]) | number[])) => void
};

const RangeThumb = ({ props, isDragged }: { props: any; isDragged: boolean }) => (
  <Box
    {...props}
    sx={{
      height: 20,
      width: 20,
      borderRadius: '50%',
      bgcolor: 'white',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      outline: '1px solid',
      outlineColor: '#d1d5db',
    }}
    style={props.style}
  >
    <Box
      sx={{
        height: 16,
        width: 4,
        borderRadius: '2px',
        bgcolor: isDragged ? '#2563eb' : '#9ca3af',
      }}
    />
  </Box>
);

export const Analytic3D = ({
  isOpen,
  onCloseDrawer,
  scatter,
  setScatter,
  view3D,
  setView3D,
  markLines,
  setMarkLines,
  nineBox,
  setNineBox,
  setSimilarKeyword,
  similarKeyword,
  setExactKeyword,
  exactKeyword,
  setKeywordList,
  colorList,
  setColorList,
  useSkillPercentage,
  setUseSkillPercentage,
  setExperienceValue,
  skillValue,
  setSkillValue,
  experienceValue,
  expTitle,
  skillTitle,
  setSvgActiveTab,
  projection,
  setProjection,
  boxWidthDepth,
  setBoxWidthDepth,
}: Analytic3DProps) => {

  const colorSelectorRef = useRef<ColorRangeSelectorRef>(null);

  useEffect(() => {
    const similarList = similarKeyword.split(':').filter(Boolean);
    const exactList = exactKeyword.split(':').filter(Boolean);
    const mergedList = Array.from(new Set([...similarList, ...exactList]));
    setKeywordList(mergedList);
  }, [similarKeyword, exactKeyword]);

  const convertStringToObjects = (
    input: string,
  ): { label: string; value: string }[] => {
    if (input === '') return [];
    return input.split(':').map((item) => ({
      label: item,
      value: item,
    }));
  };

  const handleApply = () => {
    const newColors = colorSelectorRef.current?.getColorList();
    if (newColors) {
      setColorList(newColors);
    }
    // other apply logic...
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onCloseDrawer}
      footerContent={
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" spacing={1} sx={{ width: '100%' }}>
          <Button label="Apply" onClick={handleApply} variant="primary" />
          <Button label="Close" onClick={onCloseDrawer} variant="secondary" />
        </Stack>
      }
      title="Graph Configurations"
      width="w-full md:w-[720px] xl:w-[860px]"
    >
      <Stack spacing={1.5} sx={{ bgcolor: 'white' }}>
        {/* Section: General Settings */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'grey.200',
            p: { xs: 1.5, sm: 2 },
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          <Typography sx={{ mb: 1.5, fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
            General Settings
          </Typography>

          <Stack spacing={2}>
            {/* Skill View Mode */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'space-between' }}
              spacing={1}
            >
              <Typography
                component="label"
                htmlFor="skill-view-toggle"
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#374151',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                Skill View Mode
              </Typography>
              <Box id="skill-view-toggle">
                <StatusToggle
                  options={['Skill Count', 'Skill Percentage']}
                  value={
                    useSkillPercentage ? 'Skill Percentage' : 'Skill Count'
                  }
                  onChange={(val) =>
                    setUseSkillPercentage(val === 'Skill Percentage')
                  }
                />
              </Box>
            </Stack>

            {/* Toggles */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 1.5,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <ToggleInput
                  toggled={nineBox}
                  handleToggle={() => setNineBox(!nineBox)}
                  name="nineBox"
                  informationText="9 Box Grid Overlay"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <ToggleInput
                  toggled={markLines}
                  handleToggle={() => setMarkLines(!markLines)}
                  name="markLines"
                  informationText="Guideline Grid"
                  disabled={!nineBox}
                />
              </Box>
            </Box>

            {/* Skill and Experience Value Ranges */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
                width: '100%',
                fontSize: '12px',
                color: '#374151',
              }}
            >
              <Box>
                <Typography component="output" sx={{ mb: 1, display: 'block', fontWeight: 600, fontSize: 'inherit' }}>
                  {skillTitle} ({skillValue[0]} - {skillValue[1]})
                </Typography>
                <Box sx={{ width: '100%', px: 1 }}>
                  <Range
                    values={skillValue}
                    step={1}
                    min={0}
                    max={100}
                    onChange={(newValues) => setSkillValue(newValues)}
                    renderTrack={({ props, children }) => (
                      <Box
                        {...props}
                        sx={{ height: 8, width: '100%', borderRadius: '4px' }}
                        style={{
                          ...props.style,
                          background: getTrackBackground({
                            values: skillValue,
                            colors: ['#d1d5db', '#3b82f6', '#d1d5db'],
                            min: 0,
                            max: 100,
                          }),
                        }}
                      >
                        {children}
                      </Box>
                    )}
                    renderThumb={({ props, isDragged }) => (
                      <RangeThumb props={props} isDragged={isDragged} />
                    )}
                  />
                </Box>
              </Box>

              <Box>
                <Typography component="output" sx={{ mb: 1, display: 'block', fontWeight: 600, fontSize: 'inherit' }}>
                  {expTitle} ({experienceValue[0]} - {experienceValue[1]})
                </Typography>
                <Box sx={{ width: '100%', px: 1 }}>
                  <Range
                    values={experienceValue}
                    step={1}
                    min={0}
                    max={100}
                    onChange={(newValues) => setExperienceValue(newValues)}
                    renderTrack={({ props, children }) => (
                      <Box
                        {...props}
                        sx={{ height: 8, width: '100%', borderRadius: '4px' }}
                        style={{
                          ...props.style,
                          background: getTrackBackground({
                            values: experienceValue,
                            colors: ['#d1d5db', '#3b82f6', '#d1d5db'],
                            min: 0,
                            max: 100,
                          }),
                        }}
                      >
                        {children}
                      </Box>
                    )}
                    renderThumb={({ props, isDragged }) => (
                      <RangeThumb props={props} isDragged={isDragged} />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </Stack>
        </Paper>

        {/* Separator */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5 }}>
          <Divider sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#6b7280', whiteSpace: 'nowrap' }}>
            3D Graph Usage
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Stack>

        {/* Section: 3D View Settings */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'grey.200',
            p: { xs: 1.5, sm: 2 },
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          <Typography sx={{ mb: 1.5, fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
            3D View Settings
          </Typography>

          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 1.5,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <ToggleInput
                  toggled={view3D}
                  handleToggle={() => {
                    setView3D(!view3D);
                    setSvgActiveTab(-1);
                  }}
                  name="view3D"
                  informationText="Enable 3D View"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <ToggleInput
                  toggled={scatter}
                  handleToggle={() => setScatter(!scatter)}
                  name="scatter"
                  informationText="Scatter vs Bar View"
                  disabled={!view3D}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, 1fr)' },
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="label"
                  htmlFor="colorRangeSelector"
                  sx={{ mb: 1, display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151' }}
                >
                  Keyword Color Range
                </Typography>
                <ColorRangeSelector ref={colorSelectorRef} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="label"
                  sx={{ mb: 1.5, display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151' }}
                >
                  Advanced Graph Settings
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: '1fr' },
                    gap: 2,
                    fontSize: '12px',
                    color: '#374151',
                  }}
                >
                  {/* Box Width */}
                  <Box>
                    <Typography component="output" sx={{ mb: 1, display: 'block', fontWeight: 600, fontSize: 'inherit' }}>
                      Box Width ({boxWidthDepth[0]})
                    </Typography>
                    <Box sx={{ width: '100%', px: 1 }}>
                      <Range
                        values={[boxWidthDepth[0]]}
                        step={1}
                        min={0}
                        max={500}
                        onChange={([val]) =>
                          setBoxWidthDepth(([_, d]) => [val, d])
                        }
                        renderTrack={({ props, children }) => (
                          <Box
                            {...props}
                            sx={{ height: 8, width: '100%', borderRadius: '4px' }}
                            style={{
                              ...props.style,
                              background: getTrackBackground({
                                values: [boxWidthDepth[0]],
                                colors: ['#3b82f6', '#d1d5db'],
                                min: 0,
                                max: 500,
                              }),
                            }}
                          >
                            {children}
                          </Box>
                        )}
                        renderThumb={({ props, isDragged }) => (
                          <RangeThumb props={props} isDragged={isDragged} />
                        )}
                      />
                    </Box>
                  </Box>

                  {/* Box Depth */}
                  <Box>
                    <Typography component="output" sx={{ mb: 1, display: 'block', fontWeight: 600, fontSize: 'inherit' }}>
                      Box Depth ({boxWidthDepth[1]})
                    </Typography>
                    <Box sx={{ width: '100%', px: 1 }}>
                      <Range
                        values={[boxWidthDepth[1]]}
                        step={1}
                        min={0}
                        max={500}
                        onChange={([val]) =>
                          setBoxWidthDepth(([w, _]) => [w, val])
                        }
                        renderTrack={({ props, children }) => (
                          <Box
                            {...props}
                            sx={{ height: 8, width: '100%', borderRadius: '4px' }}
                            style={{
                              ...props.style,
                              background: getTrackBackground({
                                values: [boxWidthDepth[1]],
                                colors: ['#3b82f6', '#d1d5db'],
                                min: 0,
                                max: 500,
                              }),
                            }}
                          >
                            {children}
                          </Box>
                        )}
                        renderThumb={({ props, isDragged }) => (
                          <RangeThumb props={props} isDragged={isDragged} />
                        )}
                      />
                    </Box>
                  </Box>

                  {/* Projection Toggle */}
                  <Box sx={{ gridColumn: { sm: 'span 2', xl: 'span 1' } }}>
                    <Typography component="output" sx={{ mb: 1, display: 'block', fontWeight: 600, fontSize: 'inherit' }}>
                      Projection
                    </Typography>
                    <Box sx={{ display: 'inline-block', transform: 'scale(0.95)', transformOrigin: 'left' }}>
                      <StatusToggle
                        options={['perspective', 'orthographic']}
                        value={
                          projection === 'perspective'
                            ? 'perspective'
                            : 'orthographic'
                        }
                        onChange={(val) => setProjection(val)}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Drawer>
  );

};
